import type { ModelLead, LeadsModelCrmTaskLite } from '@app/api/Leads/types';

import {
  formatLeadRegistryDateTime,
  formatLeadRegistryTaskPeriod,
} from '../../utils';

import { getLeadsRegistryResponseTime } from './getLeadsRegistryResponseTime';

import type {
  LeadsRegistryFirstTouch,
  LeadsRegistryFunnelStage,
  LeadsRegistryLegacyColumns,
  LeadsRegistryResponsibleEmployee,
  LeadsRegistryTag,
  LeadsRegistryTask,
} from '../types';

// Columns 2..N are not refactored in this iteration. `ModelLead` does not carry
// human-readable funnel stage names/colors or response-time labels, so the
// values below are derived for demonstration only and documented as temporary.
// TODO: Replace with real registry column models when the API is connected.

const FUNNEL_STAGE_FALLBACK_COLOR = '#1677ff';
const TAG_FALLBACK_COLOR = '#f5f5f5';

const FUNNEL_STAGE_PRESETS: Record<number, LeadsRegistryFunnelStage> = {
  1: {
    funnelName: 'Первичная запись',
    stageName: 'Новый запрос',
    color: '#1677ff',
  },
  2: {
    funnelName: 'Повторные клиенты',
    stageName: 'Подбор мастера',
    color: '#52c41a',
  },
  3: {
    funnelName: 'Онлайн-заявки',
    stageName: 'Уточнить контакты',
    color: '#f0ad4e',
  },
  4: { funnelName: 'Окрашивание', stageName: 'Консультация', color: '#91caff' },
  5: {
    funnelName: 'Первичная запись',
    stageName: 'Запись подтверждена',
    color: '#52c41a',
  },
  6: {
    funnelName: 'Абонементы',
    stageName: 'Согласование стоимости',
    color: '#f0ad4e',
  },
  7: {
    funnelName: 'Промо-кампания',
    stageName: 'Первый ответ',
    color: '#1677ff',
  },
  8: {
    funnelName: 'Уходовые программы',
    stageName: 'Ожидает решения',
    color: '#ff4d4f',
  },
  9: {
    funnelName: 'Первичная запись',
    stageName: 'Оплата внесена',
    color: '#52c41a',
  },
  10: {
    funnelName: 'Повторные клиенты',
    stageName: 'Закрыт успешно',
    color: '#52c41a',
  },
  11: {
    funnelName: 'Уходовые программы',
    stageName: 'Подбор услуги',
    color: '#1677ff',
  },
  12: { funnelName: 'Реактивация', stageName: 'Не отвечает', color: '#ff4d4f' },
};

const getFunnelStage = (lead: ModelLead): LeadsRegistryFunnelStage => {
  const firstStage = lead.FunnelStages?.[0];
  const firstStageId = firstStage?.Id ?? firstStage?.StageId;
  const firstFunnelId = firstStage?.SalesFunnelId;

  if (!firstStage || firstStageId === undefined) {
    return {
      funnelName: 'Без воронки',
      stageName: 'Не указан',
      color: FUNNEL_STAGE_FALLBACK_COLOR,
    };
  }

  return (
    FUNNEL_STAGE_PRESETS[firstStageId] ?? {
      funnelName: firstFunnelId ? `Воронка ${firstFunnelId}` : 'Без воронки',
      stageName: firstStage.Name || `Этап ${firstStageId}`,
      color: FUNNEL_STAGE_FALLBACK_COLOR,
    }
  );
};

const getResponsibleEmployee = (
  lead: ModelLead
): LeadsRegistryResponsibleEmployee | null => {
  if (lead.UserId === null || !lead.UserName) return null;

  return { id: lead.UserId, name: lead.UserName, role: 'Сотрудник' };
};

const getCurrentTaskStatus = (task: LeadsModelCrmTaskLite) => {
  if (task.IsCompleted) return 'completed' as const;
  if (task.IsOverdue) return 'overdue' as const;

  return 'planned' as const;
};

const getCurrentTask = (lead: ModelLead): LeadsRegistryTask | null => {
  const task = lead.CurrentCrmTask;

  if (!task) return null;

  return {
    id: task.Id,
    title: task.TaskType ?? '',
    description: task.Description,
    period: formatLeadRegistryTaskPeriod(task.TimeStart, task.TimeDeadline),
    status: getCurrentTaskStatus(task),
    taskTypeIconId: task.TaskTypeIconId,
    taskTypeColor: task.TaskTypeColor,
  };
};

const getTags = (lead: ModelLead): LeadsRegistryTag[] =>
  (lead.ClientTags ?? [])
    .filter((tag) => !tag.IsDeleted)
    .map((tag) => ({
      id: tag.Id,
      title: tag.Name ?? '',
      color: tag.Color ?? TAG_FALLBACK_COLOR,
      iconId: tag.IconId,
    }));

const getFirstTouch = (lead: ModelLead): LeadsRegistryFirstTouch => ({
  integrationType: lead.SocialIntegrationType,
  label: lead.SоcialIntegrationName ?? lead.FirstChannelId,
  dateTime: formatLeadRegistryDateTime(lead.CreateTime),
});

export const getLeadsRegistryLegacyColumns = (
  lead: ModelLead
): LeadsRegistryLegacyColumns => ({
  funnelStage: getFunnelStage(lead),
  responseTime: getLeadsRegistryResponseTime({ lead, tickSeconds: 0 }),
  responsibleEmployee: getResponsibleEmployee(lead),
  currentTask: getCurrentTask(lead),
  tags: getTags(lead),
  firstTouch: getFirstTouch(lead),
});
